import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";

export const selectPatientsState = (state: RootState) => state.patients;

export const selectPatients = (state: RootState) => state.patients.patients;
export const selectSelectedPatient = (state: RootState) =>
  state.patients.selectedPatient;
export const selectPatientsLoading = (state: RootState) =>
  state.patients.isLoading;
export const selectPatientsError = (state: RootState) => state.patients.error;
export const selectSearchQuery = (state: RootState) =>
  state.patients.searchQuery;
export const selectFilters = (state: RootState) => state.patients.filters;
export const selectPagination = (state: RootState) => state.patients.pagination;

export const selectFilteredPatients = createSelector(
  [selectPatients, selectSearchQuery, selectFilters],
  (patients, searchQuery, filters) => {
    let filtered = patients;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (patient) =>
          patient.firstName.toLowerCase().includes(query) ||
          patient.lastName.toLowerCase().includes(query) ||
          patient.email.toLowerCase().includes(query) ||
          patient.phone.includes(query)
      );
    }

    if (filters.status.length > 0) {
      filtered = filtered.filter((patient) =>
        filters.status.includes(patient.status)
      );
    }

    if (filters.gender.length > 0) {
      filtered = filtered.filter((patient) =>
        filters.gender.includes(patient.gender)
      );
    }

    if (filters.ageRange[0] > 0 || filters.ageRange[1] < 100) {
      const currentYear = new Date().getFullYear();
      filtered = filtered.filter((patient) => {
        const birthYear = new Date(patient.dateOfBirth).getFullYear();
        const age = currentYear - birthYear;
        return age >= filters.ageRange[0] && age <= filters.ageRange[1];
      });
    }

    return filtered;
  }
);

export const selectPatientsCount = createSelector(
  [selectFilteredPatients],
  (patients) => patients.length
);

export const selectPatientsByStatus = createSelector(
  [selectFilteredPatients],
  (patients) => {
    return patients.reduce((acc, patient) => {
      acc[patient.status] = (acc[patient.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
);

export const selectPatientsByGender = createSelector(
  [selectFilteredPatients],
  (patients) => {
    return patients.reduce((acc, patient) => {
      acc[patient.gender] = (acc[patient.gender] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
);

export const selectPaginatedPatients = createSelector(
  [selectFilteredPatients, selectPagination],
  (patients, pagination) => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    return patients.slice(startIndex, endIndex);
  }
);

export const selectPatientById = (id: string) =>
  createSelector([selectPatients], (patients) =>
    patients.find((patient) => patient.id === id)
  );

export const selectActivePatients = createSelector(
  [selectFilteredPatients],
  (patients) => patients.filter((patient) => patient.status === "active")
);

export const selectInactivePatients = createSelector(
  [selectFilteredPatients],
  (patients) => patients.filter((patient) => patient.status === "inactive")
);

export const selectArchivedPatients = createSelector(
  [selectFilteredPatients],
  (patients) => patients.filter((patient) => patient.status === "archived")
);

export const selectPatientsWithAllergies = createSelector(
  [selectFilteredPatients],
  (patients) => patients.filter((patient) => patient.allergies.length > 0)
);

export const selectPatientsStats = createSelector(
  [selectFilteredPatients],
  (patients) => {
    const total = patients.length;
    const active = patients.filter((p) => p.status === "active").length;
    const inactive = patients.filter((p) => p.status === "inactive").length;
    const archived = patients.filter((p) => p.status === "archived").length;
    const withAllergies = patients.filter((p) => p.allergies.length > 0).length;

    return {
      total,
      active,
      inactive,
      archived,
      withAllergies,
      withoutAllergies: total - withAllergies,
    };
  }
);
